import React from "react"
import classNames from "classnames"
import { Idea, IdeaType, HexColor } from "../../model/types"
import { XMarkIcon } from "@heroicons/react/24/solid"

/** Matches plate_viewer IDEA_CORNER_RADIUS (3px on ~30px cells). */
const CELL_ROUNDED = "rounded-[3px]"

const convertCssColourStringToRgb = (colour: string) => {
  const r = parseInt(colour.slice(1, 3), 16)
  const g = parseInt(colour.slice(3, 5), 16)
  const b = parseInt(colour.slice(5, 7), 16)
  return { r, g, b }
}

const textShouldBeWhiteOrBlack = (bgColour: string) => {
  const { r, g, b } = convertCssColourStringToRgb(bgColour)
  const brightness = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b)
  return brightness < 140
}

/**
 * Border colour matching plate_viewer `borderColourForFill`:
 * blend fill toward white/black, drawn at ~0.4 alpha.
 */
export const cellBorderColour = (fill: string): string => {
  if (!fill || fill.length < 7) return "rgba(0, 0, 0, 0.22)"
  const { r, g, b } = convertCssColourStringToRgb(fill)
  const brightness = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b)
  const target = brightness < 140 ? 255 : 0
  const amount = brightness < 140 ? 0.28 : 0.22
  const blend = (c: number) => Math.round(c + (target - c) * amount)
  return `rgba(${blend(r)}, ${blend(g)}, ${blend(b)}, 0.4)`
}

const cellBaseClass =
  "shrink-0 flex h-8 w-8 items-center justify-center text-sm overflow-hidden whitespace-nowrap border box-border"

export default function IdeaBox({
  idea,
  shadow: inShadow,
}: {
  idea: Idea
  shadow?: boolean
}) {
  const shadow = inShadow ?? true
  const fill = idea.colour || "#d1d5db"
  const isGoal = idea.type === "goal"

  return (
    <div
      className={classNames(cellBaseClass, {
        "text-white": textShouldBeWhiteOrBlack(fill),
        "text-black": !textShouldBeWhiteOrBlack(fill),
        shadow: shadow,
        "rounded-full": isGoal,
        [CELL_ROUNDED]: !isGoal,
      })}
      style={{
        backgroundColor: fill,
        borderColor: cellBorderColour(fill as HexColor),
      }}
    >
      {idea.memo || (idea.title && idea.title.slice(0, 3))}
    </div>
  )
}

export const GhostBox = ({
  type,
  shadow: inShadow,
}: {
  type: IdeaType
  shadow?: boolean
}) => {
  const shadow = inShadow ?? true
  const isGoal = type === "goal"
  return (
    <div
      className={classNames(
        cellBaseClass,
        "bg-white dark:bg-gray-800 text-transparent",
        {
          shadow: shadow,
          "rounded-full": isGoal,
          [CELL_ROUNDED]: !isGoal,
        },
      )}
      style={{
        borderColor: "rgba(0, 0, 0, 0.14)",
      }}
    />
  )
}

export const FleetingIdeaBox = () => {
  return (
    <div
      className={classNames(
        cellBaseClass,
        CELL_ROUNDED,
        "bg-gray-100 text-gray-400",
      )}
      style={{ borderColor: "rgba(0, 0, 0, 0.16)" }}
    >
      <XMarkIcon className="h-5 w-5" />
    </div>
  )
}
